import moment from 'moment'
const html2pdf = require('html2pdf.js')
import API from '@/services/api'
import { storage } from '@/services/firebase'
import { loadPDFPreview } from '@/helpers/pdfPreview.js'

const escapeHtml = value => {
    if (value == null) return ''
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

const isSpanishLanguage = language => {
    if (language == null || String(language).trim() === '') return true
    return (
        String(language)
            .trim()
            .toLowerCase() === 'spanish'
    )
}

const CERTIFICATE_TRANSLATIONS = {
    es: {
        htmlLang: 'es',
        title: 'ACTA RECIBO A SATISFACCIÓN COMPRA / SERVICIOS',
        pageTitlePrefix: 'Acta de recibo',
        fileNamePrefix: 'Acta de recibo',
        dateLabel: 'FECHA',
        dateDay: 'DD',
        dateMonth: 'MM',
        dateYear: 'AAAA',
        orderNo: 'Orden de Servicio/ Compra No:',
        supplier: 'Proveedor/ Contratista:',
        value: 'Valor:',
        plant: 'Planta:',
        intro:
            'Con el fin de liquidar la Orden de Servicio/Compra, el usuario, en calidad de verificador, determina el cumplimiento y la entrega a satisfacción del servicio/compra pactada en la orden y por la cual se realiza el acta de recibo de los mismos, cuyas características se describen a continuación.',
        productService: 'Producto/ Servicio',
        quantity: 'Cantidad',
        receptionRecords: 'Registros de Recepción',
        observations: 'Observaciones',
        closing:
            'Dejamos constancia que el proveedor ha entregado a entera satisfacción los servicios y/o bienes descritos, los cuales han sido verificados y recibidos de conformidad con lo establecido en orden de compra/ Servicio',
        signButton: 'Firmar',
        roleSupplier: 'PROVEEDOR / CONTRATISTA',
        roleReceiver: 'USUARIO QUE RECIBE',
        roleHseq: 'SUPERVISOR HSEQ',
        nameLabel: 'Nombre y Apellido:',
        idLabel: 'C.C. No.',
        generalNotesTitle: 'Nota general:',
        generalNotesBody:
            'Esta acta debe ser remitida junto al soporte de orden de servicio/compra y factura a la Carrera 72A # 107-33, Localidad Riomar, Barranquilla, Atlántico al Departamento de Almacén de Accesorios.',
        hseqNote:
            '*El visto bueno del Supervisor HSEQ aplica cuando se trate de servicios realizados dentro de la empresa.',
        excludedServicesNote:
            '*Los siguientes servicios no aplican para la firma del supervisor de HSEQ: Mantenimiento preventivo de equipos Mantenimiento de radios, Mantenimiento preventivo de impresoras 3D, Servicio de ambulancia, Servicio de tratamiento térmico de piezas de mantenimiento, Servicio de fabricación y mecanizado de piezas de mantenimiento, Toma de muestra de aceite de transformador, Instalación de black out, Devanado de los rodillos, Fabricación de piezas, Acometida manejadora de piso.',
        altDocument: 'documento',
        altSupplierSignature: 'Firma proveedor',
        altSignature: 'Firma',
        altHseqSignature: 'Firma HSEQ',
    },
    en: {
        htmlLang: 'en',
        title: 'SERVICE ACCEPTANCE CERTIFICATE',
        pageTitlePrefix: 'Acceptance Certificate',
        fileNamePrefix: 'Acceptance Certificate',
        dateLabel: 'DATE',
        dateDay: 'DD',
        dateMonth: 'MM',
        dateYear: 'YYYY',
        orderNo: 'Service/Purchase Order No:',
        supplier: 'Supplier/Contractor:',
        value: 'Value:',
        plant: 'Plant:',
        intro:
            'In order to close the Service/Purchase Order, the user, acting as verifier, confirms that the service/purchase agreed in the order has been completed and accepted, and issues this acceptance certificate, the details of which are described below.',
        productService: 'Product/Service',
        quantity: 'Quantity',
        receptionRecords: 'Reception Records',
        observations: 'Observations',
        closing:
            'We hereby certify that the supplier has satisfactorily delivered the services and/or goods described, which have been verified and accepted in accordance with the purchase/service order.',
        signButton: 'Sign',
        roleSupplier: 'SUPPLIER / CONTRACTOR',
        roleReceiver: 'RECEIVING USER',
        roleHseq: 'HSEQ SUPERVISOR',
        nameLabel: 'Full Name:',
        idLabel: 'ID No.',
        generalNotesTitle: 'General note:',
        generalNotesBody:
            'This certificate must be submitted together with the service/purchase order support and invoice to Carrera 72A # 107-33, Localidad Riomar, Barranquilla, Atlántico to the Accessories Warehouse Department.',
        hseqNote:
            '*HSEQ Supervisor approval applies when services are performed within the company premises.',
        excludedServicesNote:
            '*The following services do not require HSEQ supervisor signature: Preventive equipment maintenance, Radio maintenance, Preventive maintenance of 3D printers, Ambulance service, Heat treatment of maintenance parts, Fabrication and machining of maintenance parts, Transformer oil sampling, Blackout installation, Roller rewinding, Parts fabrication, Floor air handler connection.',
        altDocument: 'document',
        altSupplierSignature: 'Supplier signature',
        altSignature: 'Signature',
        altHseqSignature: 'HSEQ signature',
    },
}

const getCertificateTranslations = language =>
    isSpanishLanguage(language)
        ? CERTIFICATE_TRANSLATIONS.es
        : CERTIFICATE_TRANSLATIONS.en

const parseDateParts = createdOn => {
    if (!createdOn) {
        const now = moment()
        return {
            dd: now.format('DD'),
            mm: now.format('MM'),
            yyyy: now.format('YYYY'),
        }
    }
    if (typeof createdOn === 'string') {
        const m = moment(createdOn.substring(0, 10))
        return {
            dd: m.format('DD'),
            mm: m.format('MM'),
            yyyy: m.format('YYYY'),
        }
    }
    const seconds =
        createdOn._seconds || createdOn.seconds || createdOn.getTime?.() / 1000
    const m = moment.unix(seconds)
    return {
        dd: m.format('DD'),
        mm: m.format('MM'),
        yyyy: m.format('YYYY'),
    }
}

const loadDocumentPreviews = async (companyId, purchaseId, receptionEntry) => {
    const basePath = `${companyId}/purchases/${purchaseId}/receptionEntries/${receptionEntry.id}`
    const storageRef = storage().ref(basePath)
    const previews = []

    for (const doc of receptionEntry.receptionDocuments || []) {
        try {
            const url = await storageRef.child(doc.fileName).getDownloadURL()
            const isPdf =
                doc.type === 'pdf' ||
                doc.fileName?.toLowerCase().endsWith('.pdf')
            if (isPdf) {
                const pdfPreview = await loadPDFPreview(url)
                previews.push({ url: pdfPreview, isPdf: true, openUrl: url })
            } else {
                previews.push({ url, isPdf: false, openUrl: url })
            }
        } catch (error) {
            // skip missing files
        }
    }

    return previews
}

const loadSignatureUrl = async (companyId, purchaseId, receptionEntry) => {
    const basePath = `${companyId}/purchases/${purchaseId}/receptionEntries/${receptionEntry.id}`
    try {
        return await storage()
            .ref(basePath)
            .child('signature.png')
            .getDownloadURL()
    } catch (error) {
        return null
    }
}

const loadSupplierSignatureUrl = async (
    companyId,
    purchaseId,
    receptionEntry
) => {
    if (!receptionEntry.supplierSignature) {
        return null
    }
    const basePath = `${companyId}/purchases/${purchaseId}/receptionEntries/${receptionEntry.id}`
    try {
        return await storage()
            .ref(basePath)
            .child(
                receptionEntry.supplierSignature.signatureFile ||
                    'supplier-signature.png'
            )
            .getDownloadURL()
    } catch (error) {
        return null
    }
}

const loadHseqSignatureUrl = async (companyId, purchaseId, receptionEntry) => {
    if (
        receptionEntry.executionType !== 'on-site' ||
        !receptionEntry.hseqVerified
    ) {
        return null
    }
    const basePath = `${companyId}/purchases/${purchaseId}/receptionEntries/${receptionEntry.id}`
    try {
        return await storage()
            .ref(basePath)
            .child('hseq-signature.png')
            .getDownloadURL()
    } catch (error) {
        return null
    }
}

const buildItemsRows = items => {
    return (items || [])
        .map(
            item => `
        <tr>
            <td>${escapeHtml(item.description || item.code || '')}</td>
            <td style="text-align:center;">${escapeHtml(
                item.receivedQuantity
            )}</td>
        </tr>`
        )
        .join('')
}

const buildSectionBox = (title, content) => {
    const hasContent = !!(content && String(content).trim())
    return `
    <div class="section-box">
        <div class="section-box-title">${escapeHtml(title)}</div>
        <div class="section-box-content${hasContent ? '' : ' is-empty'}">${
        hasContent ? content : '&nbsp;'
    }</div>
    </div>`
}
const buildDocumentGallery = (previews, altDocument) => {
    if (!previews.length) return ''
    return `<div class="doc-gallery">${previews
        .map(
            preview => `
        <a href="${escapeHtml(
            preview.openUrl || preview.url
        )}" target="_blank" rel="noopener noreferrer" class="doc-link">
            <img src="${preview.url}" alt="${escapeHtml(
                altDocument
            )}" class="doc-thumb" />
        </a>`
        )
        .join('')}</div>`
}

const buildReceiptCertificate = async ({
    receptionEntry,
    companyId,
    purchaseId,
    companyLogo = null,
    showSupplierSignButton = false,
    language = 'Spanish',
}) => {
    const t = getCertificateTranslations(language)

    let logo = companyLogo || ''
    if (!logo) {
        try {
            const {
                data: { settings },
            } = await API.getSettings()
            const company =
                settings.find(s => s.name === 'Company')?.generalInfo || {}
            logo = company.logoBase64 || company.logo || ''
        } catch (error) {
            logo = ''
        }
    }

    const dateParts = parseDateParts(receptionEntry.createdOn)
    const documentPreviews = await loadDocumentPreviews(
        companyId,
        purchaseId,
        receptionEntry
    )
    const signatureUrl = await loadSignatureUrl(
        companyId,
        purchaseId,
        receptionEntry
    )
    const hseqSignatureUrl = await loadHseqSignatureUrl(
        companyId,
        purchaseId,
        receptionEntry
    )
    const supplierSignatureUrl = await loadSupplierSignatureUrl(
        companyId,
        purchaseId,
        receptionEntry
    )

    const supplier = receptionEntry.supplierSignature || {}
    const supplierName = supplier.name || ''
    const supplierDoc = supplier.documentId || ''
    const receiver = receptionEntry.receiverSignature || {}
    const receiverName = receiver.name || receptionEntry.receivedBy?.name || ''
    const receiverDoc = receiver.documentId || ''
    const isOnSite = receptionEntry.executionType === 'on-site'
    const hseqSupervisor = receptionEntry.hseqSignature || {}
    const hseqName =
        hseqSupervisor.name || receptionEntry.hseqVerifiedBy?.name || ''
    const hseqDoc = hseqSupervisor.documentId || ''

    const supplierSignatureBlock = supplierSignatureUrl
        ? `<img class="signature-img" src="${supplierSignatureUrl}" alt="${escapeHtml(
              t.altSupplierSignature
          )}" />`
        : showSupplierSignButton
        ? `<button type="button" id="sign-supplier-btn" class="sign-supplier-btn">${escapeHtml(
              t.signButton
          )}</button>`
        : '<div class="signature-line"></div>'

    const htmlContent = `
<!DOCTYPE html>
<html lang="${t.htmlLang}">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${escapeHtml(t.pageTitlePrefix)} - ${escapeHtml(
        receptionEntry.orderNumber || ''
    )}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 32px 120px;
            color: #111;
            font-size: 13px;
        }
        .content {
            max-width: 640px;
            margin: 0 auto;
        }
        .header {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 20px;
            text-align: center;
        }
        .logo {
            margin-bottom: 10px;
        }
        .logo img {
            height: 40px;
            width: auto;
            display: block;
            margin: 0 auto;
        }
        .title {
            text-align: center;
            font-weight: bold;
            font-size: 15px;
            letter-spacing: 0.2px;
            text-transform: uppercase;
        }
        .date-label {
            width: 220px;
            text-align: center;
            font-weight: bold;
            font-size: 12px;
            margin-bottom: 2px;
        }
        table.date-table {
            width: 220px;
            border-collapse: collapse;
            margin-bottom: 12px;
        }
        table.date-table th,
        table.date-table td {
            border: 1px solid #000;
            padding: 2px 6px;
            line-height: 1.2;
        }
        table.date-table th {
            text-align: center;
            font-size: 11px;
            background-color: #d9d9d9;
            font-weight: bold;
        }
        table.data-table {
            width: 100%;
            border-collapse: collapse;
        }
        table.data-table th,
        table.data-table td {
            border: 1px solid #000;
            padding: 2px 6px;
            line-height: 1.2;
        }
        table.data-table th {
            background-color: #d9d9d9;
            font-weight: bold;
            text-align: center;
        }
        .info-table td:first-child {
            width: 38%;
            background-color: #d9d9d9;
            font-weight: bold;
        }
        .items-table th:last-child,
        .items-table td:last-child {
            width: 90px;
            text-align: center;
        }
        .intro {
            margin: 14px 0;
            text-align: justify;
            line-height: 1.4;
        }
        .section-box {
            border: 1px solid #000;
            margin-top: 12px;
            margin-bottom: 12px;
        }
        .section-box-title {
            border-bottom: 1px solid #000;
            text-align: center;
            font-weight: bold;
            padding: 2px 6px;
            line-height: 1.2;
            background-color: #d9d9d9;
        }
        .section-box-content {
            padding: 4px 6px;
            line-height: 1.3;
        }
        .section-box-content.is-empty {
            min-height: calc((1.2em + 4px) * 2);
            padding: 2px 6px;
        }
        .doc-gallery {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            align-items: flex-start;
        }
        .doc-link {
            display: block;
            cursor: pointer;
            line-height: 0;
        }
        .doc-thumb {
            display: block;
            max-width: 100px;
            max-height: 80px;
            object-fit: contain;
        }
        .doc-link:hover .doc-thumb {
            opacity: 0.85;
        }
        .closing {
            margin: 14px 0 24px;
            text-align: justify;
            line-height: 1.4;
        }
        .signatures {
            display: flex;
            justify-content: space-between;
            gap: 16px;
            margin-top: 24px;
        }
        .signature-block {
            flex: 1;
            text-align: center;
        }
        .signature-line {
            border-top: 1px solid #000;
            margin: 48px 8px 8px;
            min-height: 1px;
        }
        .signature-img {
            max-height: 48px;
            max-width: 160px;
            margin-bottom: 4px;
            object-fit: contain;
        }
        .sign-supplier-btn {
            display: inline-block;
            margin: 24px 0 8px;
            padding: 8px 20px;
            font-size: 14px;
            color: #fff;
            background-color: #2074d4;
            border: none;
            border-radius: 4px;
            cursor: pointer;
        }
        .sign-supplier-btn:hover {
            background-color: #185a9e;
        }
        .signature-role {
            font-weight: bold;
            font-size: 12px;
            margin-bottom: 8px;
        }
        .signature-field {
            font-size: 12px;
            margin: 2px 0;
        }
        .general-notes {
            margin-top: 28px;
            font-size: 11px;
            line-height: 1.35;
            text-align: justify;
        }
        .general-notes p {
            margin: 0 0 8px;
        }
        .general-notes .note-item {
            margin: 0 0 6px;
        }
    </style>
</head>
<body>
    <div class="content">
    <div class="header">
        <div class="logo">
            ${logo ? `<img src="${logo}" alt="Logo" />` : ''}
        </div>
        <div class="title">
            ${escapeHtml(t.title)}
        </div>
    </div>

    <div class="date-label">${escapeHtml(t.dateLabel)}</div>
    <table class="date-table">
        <tr>
            <th>${escapeHtml(t.dateDay)}</th>
            <th>${escapeHtml(t.dateMonth)}</th>
            <th>${escapeHtml(t.dateYear)}</th>
        </tr>
        <tr>
            <td style="text-align:center;">${dateParts.dd}</td>
            <td style="text-align:center;">${dateParts.mm}</td>
            <td style="text-align:center;">${dateParts.yyyy}</td>
        </tr>
    </table>

    <table class="data-table info-table">
        <tr>
            <td>${escapeHtml(t.orderNo)}</td>
            <td>${escapeHtml(receptionEntry.orderNumber)}</td>
        </tr>
        <tr>
            <td>${escapeHtml(t.supplier)}</td>
            <td>${escapeHtml(receptionEntry.supplier)}</td>
        </tr>
        <tr>
            <td>${escapeHtml(t.value)}</td>
            <td>${escapeHtml(receptionEntry.value)}</td>
        </tr>
        <tr>
            <td>${escapeHtml(t.plant)}</td>
            <td>${escapeHtml(receptionEntry.plant)}</td>
        </tr>
    </table>

    <p class="intro">
        ${escapeHtml(t.intro)}
    </p>

    <table class="data-table items-table">
        <tr>
            <th>${escapeHtml(t.productService)}</th>
            <th>${escapeHtml(t.quantity)}</th>
        </tr>
        ${buildItemsRows(receptionEntry.items)}
    </table>

    ${buildSectionBox(
        t.receptionRecords,
        buildDocumentGallery(documentPreviews, t.altDocument)
    )}

    ${buildSectionBox(
        t.observations,
        escapeHtml(receptionEntry.observations || '')
    )}

    <p class="closing">
        ${escapeHtml(t.closing)}
    </p>

    <div class="signatures">
        <div class="signature-block">
            ${supplierSignatureBlock}
            <div class="signature-role">${escapeHtml(t.roleSupplier)}</div>
            <div class="signature-field">${escapeHtml(
                t.nameLabel
            )} ${escapeHtml(supplierName)}</div>
            <div class="signature-field">${escapeHtml(t.idLabel)} ${escapeHtml(
        supplierDoc
    )}</div>
        </div>
        <div class="signature-block">
            ${
                signatureUrl
                    ? `<img class="signature-img" src="${signatureUrl}" alt="${escapeHtml(
                          t.altSignature
                      )}" />`
                    : '<div class="signature-line"></div>'
            }
            <div class="signature-role">${escapeHtml(t.roleReceiver)}</div>
            <div class="signature-field">${escapeHtml(
                t.nameLabel
            )} ${escapeHtml(receiverName)}</div>
            <div class="signature-field">${escapeHtml(t.idLabel)} ${escapeHtml(
        receiverDoc
    )}</div>
        </div>
        ${
            isOnSite
                ? `<div class="signature-block">
            ${
                hseqSignatureUrl
                    ? `<img class="signature-img" src="${hseqSignatureUrl}" alt="${escapeHtml(
                          t.altHseqSignature
                      )}" />`
                    : '<div class="signature-line"></div>'
            }
            <div class="signature-role">${escapeHtml(t.roleHseq)}</div>
            <div class="signature-field">${escapeHtml(
                t.nameLabel
            )} ${escapeHtml(hseqName)}</div>
            <div class="signature-field">${escapeHtml(t.idLabel)} ${escapeHtml(
                      hseqDoc
                  )}</div>
        </div>`
                : ''
        }
    </div>

    <div class="general-notes">
        <p>
            <strong>${escapeHtml(t.generalNotesTitle)}</strong> ${escapeHtml(
        t.generalNotesBody
    )}
        </p>
        <p class="note-item">
            ${escapeHtml(t.hseqNote)}
        </p>
        <p class="note-item">
            ${escapeHtml(t.excludedServicesNote)}
        </p>
    </div>
    </div>
</body>
</html>`

    const fileName = `${t.fileNamePrefix} - ${receptionEntry.orderNumber || ''}`

    return { htmlContent, fileName }
}

export const generateReceiptCertificate = async ({
    receptionEntry,
    companyId,
    purchaseId,
    language = 'Spanish',
}) => {
    const { htmlContent, fileName } = await buildReceiptCertificate({
        receptionEntry,
        companyId,
        purchaseId,
        language,
    })

    const newWindow = window.open('', '_blank')
    newWindow.document.write(htmlContent)
    newWindow.document.title = fileName
    newWindow.document.close()
}

export const downloadReceiptCertificate = async ({
    receptionEntry,
    companyId,
    purchaseId,
    language = 'Spanish',
}) => {
    const { htmlContent, fileName } = await buildReceiptCertificate({
        receptionEntry,
        companyId,
        purchaseId,
        language,
    })

    const container = document.createElement('div')
    container.innerHTML = htmlContent
    document.body.appendChild(container)

    const options = {
        margin: [15, 0, 15, 0],
        filename: `${fileName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
        pagebreak: {
            mode: ['css', 'legacy'],
            avoid: ['tr', 'th', 'td', '.section-box', '.signature-block'],
        },
    }

    try {
        await html2pdf()
            .from(container)
            .set(options)
            .save()
    } finally {
        document.body.removeChild(container)
    }
}

export const getReceiptCertificatePdfFile = async ({
    receptionEntry,
    companyId,
    purchaseId,
    companyLogo = null,
    language = 'Spanish',
}) => {
    const { htmlContent, fileName } = await buildReceiptCertificate({
        receptionEntry,
        companyId,
        purchaseId,
        companyLogo,
        language,
    })

    const pdfFileName = `${fileName}.pdf`
    const container = document.createElement('div')
    container.innerHTML = htmlContent
    document.body.appendChild(container)

    const options = {
        margin: [15, 0, 15, 0],
        filename: pdfFileName,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
        pagebreak: {
            mode: ['css', 'legacy'],
            avoid: ['tr', 'th', 'td', '.section-box', '.signature-block'],
        },
    }

    try {
        const pdf = await html2pdf()
            .from(container)
            .set(options)
            .outputPdf('blob')
        const blob =
            pdf instanceof Blob
                ? pdf
                : new Blob([pdf], { type: 'application/pdf' })
        return {
            file: new File([blob], pdfFileName, { type: 'application/pdf' }),
            fileName: pdfFileName,
        }
    } finally {
        document.body.removeChild(container)
    }
}

export { buildReceiptCertificate, isSpanishLanguage }
